import { EventEmitter, ChangeDetectionStrategy, Input, Output, Component, OnInit, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { PrimeColumn } from "@common/models/prime-column";
import { ColumnDefinitionFormatType as FormatType } from "@common/enums/column-definition-format-type";
import { MfsUser } from "@feature/_app_modules/mojo-for-suppliers/user-admin/models/mfs-user";
import { MftUser } from "@feature/_app_modules/mojo-for-tenants/user-admin/models/mft-user";
import { ExportCsvService } from "@shared/export-csv/services/export-csv-data.service";
import { QueryBuilderConfig, RuleSet }from '@feature/_app_modules/ngx-angular-query-builder/query-builder/query-builder.interfaces';
import {
  FilterEditorDialogComponent,
  FilterEditorDialogResult,
  getListTypeFilter,
  QueryBuilderConfigProvider
} from "@feature/common";
import { ListType } from "@common/enums/list-type";
import * as filterActions from "@common/filter/actions/filter.actions";
import {ApplicationState} from "@store/application-store";
import { Store, select } from "@ngrx/store";
import { UserFilterFieldProvider } from "./filter/user-filter-field-provider";
import {StandardFilterStrategy} from "@common/filter/standard-filter-strategy";
import { distinctUntilChanged } from "rxjs";
import * as filterUtils from "@common/filter/filter-utils";

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'mojo-user-list',
    templateUrl: './user-list.component.html',
    standalone: false
})
export class UserListComponent
  implements OnInit, OnChanges {

  @Input()
  formatValue: (col: PrimeColumn, data: MfsUser | MftUser) => any;

  @Input()
  columns: PrimeColumn[] = [];

  @Input()
  rowsToShow = 10;

  @Input()
  loading: boolean;

  @Input()
  users: (MfsUser | MftUser)[] = [] ;

  @Input()
  selectedUsers: (MfsUser | MftUser)[] = [];

  @Input({ required: true })
  listType: (ListType.MfsManageMyUsers | ListType.MftManageMyUsers);

  @Input()
  dataKey: string;

  @Input()
  displayExportButton = true;

  @Output()
  onSelectionChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild(FilterEditorDialogComponent, { static: true }) filterDialog;

  queryConfig: QueryBuilderConfig;
  currentFilter: RuleSet;
  filteredUsers: (MfsUser | MftUser)[];

  selectAllState: 'none' | 'indeterminate' | 'all' = 'none';
  selectAll = false;

  constructor(
    private readonly exportCsvService: ExportCsvService,
    private readonly store: Store<ApplicationState>) {
  }

  ngOnInit() {
    this.filteredUsers = this.users;
    this.selectedUsers = [];
    this.updateSelectAllState();
    this.store.dispatch(new filterActions.LoadListTypeFilter(new filterActions.ListTypeFilterActionPayload(this.listType)));
    this.store
      .pipe(select(getListTypeFilter(this.listType)))
      .pipe(distinctUntilChanged())
      .subscribe((f: RuleSet) => {
        this.currentFilter = f;
        this.applyUsersOrFilterUpdate();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users?.previousValue !== changes.users?.currentValue) {
      this.applyUsersOrFilterUpdate();
    }
  }

  applyUsersOrFilterUpdate() : void {
    const userFilterFieldProvider : UserFilterFieldProvider = new UserFilterFieldProvider(this.users);
    const configBuilder : QueryBuilderConfigProvider = new QueryBuilderConfigProvider(userFilterFieldProvider);
    this.queryConfig = configBuilder.getQueryBuilderConfig();
    const fs : StandardFilterStrategy<MfsUser | MftUser> = new StandardFilterStrategy<MfsUser | MftUser>(userFilterFieldProvider);
    this.filteredUsers = fs.applyFilter(this.users, this.currentFilter) ?? [];
    this.selectedUsers = [];
    this.updateSelectAllState();
  }

  needDateFormat(col: PrimeColumn) {
    return col.type === FormatType.Date;
  }

  isRowSelected(rowData) {
    return this.selectedUsers.filter(x => x.id === rowData[this.dataKey]).length > 0;
  }

  onRowSelect() {
    this.updateSelectAllState();
    this.onSelectionChanged.emit(this.selectedUsers);
  }

  onRowUnSelect() {
    this.updateSelectAllState();
    this.onSelectionChanged.emit(this.selectedUsers);
  }

  itemLevelColumns() {
    return this.columns.map(column => {return {Label: column.header, FieldName: column.field, Type: null}});
  }

  export = () : void =>
    this.exportCsvService.export(this.filteredUsers, this.itemLevelColumns());

  toggleSelectAll(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedUsers = [...this.filteredUsers];
    } else {
      this.selectedUsers = [];
    }
    this.onSelectionChanged.emit(this.selectedUsers);
    this.updateSelectAllState();
  }

  toggleRowSelection(rowData) {
    const rowSelected = this.isRowSelected(rowData);
    if (rowSelected) {
      this.selectedUsers = this.selectedUsers.filter(x => x[this.dataKey] !== rowData[this.dataKey]);
    } else {
      this.selectedUsers = [...this.selectedUsers, rowData];
    }
    this.updateSelectAllState();
    this.onSelectionChanged.emit(this.selectedUsers);
  }

  updateSelectAllState() {
    if (this.selectedUsers.length === this.filteredUsers.length && this.filteredUsers.length > 0) {
      this.selectAllState = 'all';
    } else if (this.selectedUsers.length > 0 && this.selectedUsers.length < this.filteredUsers.length) {
      this.selectAllState = 'indeterminate';
    } else {
      this.selectAllState = 'none';
    }
  }

  filterEdited($event: FilterEditorDialogResult) : void {
    this.store.dispatch(new filterActions.SaveListTypeFilter(new filterActions.ListTypeFilterActionPayload(this.listType, $event.filter)));
  }

  showFilterDialog(): void {
    this.filterDialog.showFilterDialog(this.currentFilter);
  }

  get hasCustomFilter(): boolean {
    return filterUtils.isNotDefaultFilter(this.currentFilter);
  }

}
